'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { GhgAuditPage } from '@/features/ghg/audit/components/GhgAuditPage';
import type { AuditExportFormat, AuditScopeFilter, AuditSourceTypeFilter } from '@/features/ghg/audit/types';
import i18n from '@/lib/i18n';

const auditSearchSchema = z.object({
  section: z
    .enum([
      'audit-pack',
      'reports',
      'emission-factors',
      'source-data',
      'change-log',
      'methodology-assumptions',
      'supporting-documents',
    ])
    .default('audit-pack')
    .catch('audit-pack'),
  scope: z.enum(['all', '1', '2', '3']).default('all').catch('all'),
  category: z.string().default('all').catch('all'),
  entityId: z.string().default('').catch(''),
  recordStatus: z.string().default('all').catch('all'),
  sourceType: z
    .enum(['all', 'manual', 'import', 'ai_extraction', 'integration', 'unknown'])
    .default('all')
    .catch('all'),
  format: z.enum(['zip', 'json', 'csv']).default('zip').catch('zip'),
});

export const Route = createFileRoute('/_authenticated/ghg/audit')({
  validateSearch: auditSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.ghgAuditCenter'),
  }),
  component: GhgAuditRoute,
});

function GhgAuditRoute() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  return (
    <GhgAuditPage
      section={search.section}
      scope={search.scope as AuditScopeFilter}
      category={search.category}
      entityId={search.entityId}
      recordStatus={search.recordStatus}
      sourceType={search.sourceType as AuditSourceTypeFilter}
      format={search.format as AuditExportFormat}
      onSectionChange={(section) => {
        void navigate({
          to: '/ghg/audit',
          search: {
            ...search,
            section,
          },
          replace: true,
        });
      }}
      onScopeChange={(scope) => {
        void navigate({
          to: '/ghg/audit',
          search: {
            ...search,
            scope,
          },
          replace: true,
        });
      }}
      onCategoryChange={(category) => {
        void navigate({
          to: '/ghg/audit',
          search: {
            ...search,
            category,
          },
          replace: true,
        });
      }}
      onEntityChange={(entityId) => {
        void navigate({
          to: '/ghg/audit',
          search: {
            ...search,
            entityId,
          },
          replace: true,
        });
      }}
      onRecordStatusChange={(recordStatus) => {
        void navigate({
          to: '/ghg/audit',
          search: {
            ...search,
            recordStatus,
          },
          replace: true,
        });
      }}
      onSourceTypeChange={(sourceType) => {
        void navigate({
          to: '/ghg/audit',
          search: {
            ...search,
            sourceType,
          },
          replace: true,
        });
      }}
      onFormatChange={(format) => {
        void navigate({
          to: '/ghg/audit',
          search: {
            ...search,
            format,
          },
          replace: true,
        });
      }}
    />
  );
}
